import { useCallback, useEffect, useState } from 'react';
import { useSaveToasts } from '../use-save-toasts';
import { useParams } from 'react-router-dom';
import {
  currencySettingsSchema,
  externalCostSchema,
  lineItemSchema,
  metafieldSchema,
  occurrenceEconomicsSchema,
  type ExternalCost,
  type LineItem,
  type Metafield,
  type SaveExternalCostRequest,
} from '@forecastify/shared';
import { Button, Icon } from '@forecastify/ui';
import { useT } from '../../i18n';
import { CONTRACT_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useFirstLoad } from '../use-first-load';
import { useSession } from '../../session';
import { useGoBack, useWorkspaceNavigate } from '../../workspace-path';
import { Breadcrumb } from '../breadcrumb';
import { usePageHeader } from '../page-header';
import { parseList } from '../permissions-hook';
import { ScreenState } from '../screen-state';
import { costCrumbs } from './money-crumbs';
import { ExternalCostDetail } from './external-cost-detail';
import {
  ExternalCostDialog,
  bodyOfCost,
  draftOf,
  type ExternalCostDraft,
} from './external-cost-dialog';
import { lineItemContractLabel } from './contract-label';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C62 · `/costs/:number` — one external cost, at an address somebody can send.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THERE IS NO WORKSPACE-LEVEL COST LIST, AND THIS IS NOT ONE.** `PHASE-4.md` records the
 * mockup's shape: a cost lives on its project's tab, and only the single record gets a full view.
 * So this route is reached from that tab, from the line item's section and from a pasted link —
 * never from the sidebar, which is what `entrance: 'none'` says out loud.
 *
 * ⚠️ **Keyed on the identifier (`ec_7c2d9e01`, R-57), like the invoice beside it.** The old
 * number form (`EK-3041`) still resolves and is rewritten in place.
 *
 * ⚠️⚠️ **THE PAGE KEEPS ITS OWN MARKER EVEN WHEN THE NUMBER IS UNKNOWN** — the lesson
 * `/contracts/:code` learned: a surface that only exists when something matched makes a bookmark to
 * a deleted row silently render as something else at an address that says otherwise.
 */
export function CostPageRoute() {
  const t = useT();
  /* 26 Oct 2026 · the cost dialog's success toast. */
  const { saved, toaster } = useSaveToasts();
  const go = useWorkspaceNavigate();
  const session = useSession();
  const params = useParams();

  const [cost, setCost] = useState<ExternalCost | null>(null);
  const [metafields, setMetafields] = useState<readonly Metafield[]>([]);
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);
  const [currencies, setCurrencies] = useState<readonly { code: string; symbol: string }[]>([]);
  const [editing, setEditing] = useState<ExternalCostDraft | null>(null);
  /* ux-pass-2 · the detail opens the dialog for ONE question — the link, or the recurrence. */
  const [focus, setFocus] = useState<'link' | 'recurrence' | undefined>(undefined);
  const [periods, setPeriods] = useState<readonly { startsOn: string; label: string }[]>([]);
  const goBack = useGoBack();
  const [lineItems, setLineItems] = useState<readonly LineItem[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * R-57 · the identifier is the address; `EK-3041` (or a bare number) still resolves — on the
   * server (`GET /external-costs/:ref`). 31 Oct 2026 · this read every cost in the workspace and
   * found the one, on the argument that they were counted in tens and the list was not paged.
   */
  const ref = params.number ?? '';
  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null || ref === '') return;
    const response = await session
      .request(`/external-costs/${encodeURIComponent(ref)}`)
      .catch(() => null);
    /* ⚠️ *Unknown cost* only when the server says so — a failed read keeps what is open. */
    if (response?.status === 404) {
      setCost(null);
      return;
    }
    if (response?.ok !== true) return;
    setCost(externalCostSchema.safeParse(await response.json()).data ?? null);
  }, [session, ref]);

  /* The first load says so rather than "unknown cost" — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    void reload();
  });

  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const [fieldResponse, memberResponse, currencyResponse, itemResponse] = await Promise.all([
        session.request('/metafields?entity=external_cost').catch(() => null),
        requestAll(session, '/people').catch(() => null),
        session.request('/workspace/currencies').catch(() => null),
        /* ⚠️ The edit dialog's link picker needs them. Fetched here rather than when the dialog
           opens, because a picker that fills in a beat after the form does looks broken. */
        session.request('/line-items').catch(() => null),
      ]);

      if (itemResponse?.ok === true) {
        setLineItems(parseList(lineItemSchema, await itemResponse.json()) ?? []);
      }

      if (fieldResponse?.ok === true) {
        setMetafields(parseList(metafieldSchema, await fieldResponse.json()) ?? []);
      }
      if (memberResponse?.ok === true) {
        const body = (await memberResponse.json()) as { data?: unknown };
        if (Array.isArray(body.data)) {
          setMembers(
            body.data
              .filter(
                (one): one is { id: string; displayName: string } =>
                  typeof one === 'object' &&
                  one !== null &&
                  typeof (one as { id?: unknown }).id === 'string',
              )
              .map((one) => ({ id: one.id, name: one.displayName })),
          );
        }
      }
      if (currencyResponse?.ok === true) {
        const parsed = currencySettingsSchema.safeParse(await currencyResponse.json());
        if (parsed.success) {
          setCurrencies(
            parsed.data.currencies.map((one) => ({ code: one.code, symbol: one.symbol })),
          );
        }
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /* 22 Oct 2026 · the server's answer for this cost (`project-reach.ts`), computed below. */

  /* R-57 · resolved from the old number, so the bar is rewritten to the identifier. */
  useEffect(() => {
    if (cost === null || params.number === cost.identifier) return;
    go(`/costs/${cost.identifier}`, { replace: true });
  }, [cost, params.number, go]);
  const mine = lineItems.filter((one) => one.projectId === cost?.projectId);

  /**
   * ⚠️ The periods of the contract the dialog opened on — the occurrence arm's picker needs them,
   * and this page passed none, so a period-linked cost could not be re-pointed from here at all.
   * The project tab's own rule: read when the pick changes, not every contract's up front.
   */
  const pickedContract = editing?.contractId ?? '';
  useEffect(() => {
    setPeriods([]);
    if (pickedContract === '' || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/contracts/${pickedContract}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(occurrenceEconomicsSchema, await response.json()) ?? [];
      setPeriods(
        parsed.map((one) => ({
          startsOn: one.startsOn,
          label: `#${String(one.index)} · ${one.startsOn}`,
        })),
      );
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [pickedContract, session.workspaceId]);

  /**
   * ⚠️⚠️ **THE PAGE CLAIMS THE SHELL'S HEADER, WITH A TRAIL — ux-pass-2, 14 Oct.** *"Also
   * breadcrumb is missing in both places, fix it."* This page drew no header at all, so the shell
   * fell back to the route's title. The trail is `money-crumbs.ts`'s — project, then the contract
   * and the line item or period the cost is attached to — and the chevron returns where the reader
   * came from (`useGoBack`), the project only when there is nothing behind this page.
   */
  usePageHeader(
    () =>
      cost === null ? null : (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1.5"
            aria-label={t('task.action.back')}
            onClick={() => {
              goBack(`/projects/${cost.projectId}`);
            }}
          >
            <Icon name="chevron-left" size={16} />
          </Button>
          <Breadcrumb copyValue={cost.identifier} crumbs={costCrumbs(cost, t, go)} />
        </>
      ),
    /* ⚠️ VALUES, NOT CALLBACKS — `hard-won.md` C24-1. */
    [
      cost?.identifier,
      cost?.name,
      cost?.projectName,
      cost?.linkKind,
      cost?.contractIdentifier,
      cost?.lineItemName,
      cost?.occurrenceStartsOn,
    ],
    /* Until it resolves, the shell's route title stands. */
    cost !== null,
  );

  return (
    <ScreenState failure={null} onRetry={() => void reload()}>
      <div data-screen="cost" className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {cost === null ? (
          <p className="text-muted-foreground p-5 text-[12.5px]">
            {loading ? t('state.loading') : t('extcost.unknown')}
          </p>
        ) : (
          <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
            {serverError === null ? null : (
              <p className="text-destructive px-5 pt-3 text-[12px]">{serverError}</p>
            )}
            <ExternalCostDetail
              wide
              data={{ cost, metafields, members, currencies }}
              handlers={{
                canManage: cost.canManage,
                onPatch: (change) => {
                  void save(cost.id, bodyOfCost(cost, change));
                },
                onEditLink: () => {
                  setFocus('link');
                  setEditing(draftOf(cost));
                },
                onEditRecurrence: () => {
                  setFocus('recurrence');
                  setEditing(draftOf(cost));
                },
                onDuplicate: () => {
                  setFocus(undefined);
                  setEditing({ ...draftOf(cost), id: null });
                },
                onDelete: () => {
                  void (async () => {
                    await write(`/external-costs/${cost.id}`, { method: 'DELETE' });
                    go(`/projects/${cost.projectId}`);
                  })();
                },
                onMetafields: (values) => {
                  void save(cost.id, bodyOfCost(cost, { metafields: values }));
                },
                onOpenProject: () => {
                  go(`/projects/${cost.projectId}`);
                },
                onOpenLink: () => {
                  if (cost.linkKind === 'line_item') {
                    if (cost.contractIdentifier !== null && cost.lineItemIdentifier !== null) {
                      go(`/contracts/${cost.contractIdentifier}/${cost.lineItemIdentifier}`);
                    }
                    return;
                  }
                  if (cost.linkKind === 'occurrence' && cost.contractIdentifier !== null) {
                    go(
                      `/contracts/${cost.contractIdentifier}/periods/${cost.occurrenceStartsOn ?? ''}`,
                    );
                  }
                },
                onOpenOwner: () => {
                  go(`/people/${cost.ownerMemberId}`);
                },
                onOpenPeriod: (startsOn) => {
                  if (cost.contractIdentifier !== null) {
                    go(`/contracts/${cost.contractIdentifier}/periods/${startsOn}`);
                  }
                },
                onAttach: async (file) => {
                  const form = new FormData();
                  form.set('file', file);
                  const response = await session
                    .request(`/files?target=external_cost&targetId=${cost.id}`, {
                      method: 'POST',
                      body: form,
                    })
                    .catch(() => null);
                  return response?.ok === true ? null : 'upload_failed';
                },
              }}
            />
          </div>
        )}
      </div>

      {editing === null ? null : (
        <ExternalCostDialog
          draft={editing}
          currencies={currencies.map((one) => ({ ...one, rateToBase: '1' }))}
          /* ⚠️ Only this project's — a cost's line-item arm can only point at one of them, and the
             occurrence arm at one of its contracts. */
          lineItems={mine.map((one) => ({ id: one.id, name: one.name }))}
          contracts={[
            ...new Map(mine.map((one) => [one.contractId, lineItemContractLabel(one)])).entries(),
          ].map(([id, code]) => ({ id, code }))}
          periods={periods}
          members={members}
          serverError={serverError}
          {...(focus === undefined ? {} : { focus })}
          onCancel={() => {
            setEditing(null);
            setFocus(undefined);
          }}
          onSave={(body) => {
            void save(editing.id, body);
          }}
        />
      )}
      {toaster}
    </ScreenState>
  );

  /** 26 Oct 2026 · resolves to whether it landed — the dialog closes on `true` only. */
  async function write(path: string, init: RequestInit): Promise<boolean> {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      /* ⚠️ A stale write is its own sentence: an in-place edit races the live reload. */
      setServerError(response?.status === 409 ? t('error.conflict') : t('error.unavailable'));
      return false;
    }
    await reload();
    return true;
  }

  /** ⚠️ `null` is the DUPLICATE — the same branch the project tab's saver takes. */
  async function save(id: string | null, body: SaveExternalCostRequest): Promise<void> {
    const landed = await write(id === null ? '/external-costs' : `/external-costs/${id}`, {
      method: id === null ? 'POST' : 'PUT',
      body: JSON.stringify(body),
    });
    /* 26 Oct 2026 · ⚠️ it closed whatever the answer, so a refusal was never seen. It stays
       open now, with the refusal in it, and a save that landed says so. */
    if (!landed) return;
    setEditing(null);
    setFocus(undefined);
    saved(
      id === null
        ? t('common.toast.created', { name: body.name })
        : t('common.toast.saved', { name: body.name }),
    );
  }
}
